import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const Shell = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-background p-6">
    <div className="w-full max-w-md space-y-6 animate-fade-up rounded-3xl border bg-card p-8 shadow-sm" data-testid="reset-password-page">
      <img src="/DietFlix-logo.webp" alt="DietFlix" className="h-9" />
      {children}
    </div>
  </div>
);

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get("/auth/reset-password/verify", { params: { token } })
      .then((r) => setState(r.data.valid ? "form" : "invalid"))
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("La password deve contenere almeno 8 caratteri");
    if (password !== confirm) return setError("Le due password non coincidono");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setState("done");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (state === "checking") return <Shell><div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Verifica del link…</div></Shell>;

  if (state === "invalid") return (
    <Shell>
      <div className="space-y-3" data-testid="reset-invalid">
        <h1 className="font-heading text-2xl font-semibold flex items-center gap-2"><AlertTriangle className="h-6 w-6 text-destructive" /> Link non valido</h1>
        <p className="text-muted-foreground text-sm">Il link è scaduto o è già stato utilizzato. Richiedine uno nuovo dalla pagina di accesso con "Password dimenticata?".</p>
        <Button asChild className="w-full"><Link to="/" data-testid="reset-go-login">Vai all'accesso</Link></Button>
      </div>
    </Shell>
  );

  if (state === "done") return (
    <Shell>
      <div className="space-y-3" data-testid="reset-success">
        <h1 className="font-heading text-2xl font-semibold flex items-center gap-2"><CheckCircle2 className="h-6 w-6 text-primary" /> Password aggiornata</h1>
        <p className="text-muted-foreground text-sm">La tua nuova password è attiva. Per sicurezza abbiamo chiuso le sessioni aperte sugli altri dispositivi.</p>
        <Button asChild className="w-full"><Link to="/" data-testid="reset-go-login">Accedi ora</Link></Button>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div>
        <h1 className="font-heading text-2xl font-semibold">Scegli una nuova password</h1>
        <p className="text-muted-foreground text-sm mt-1">Almeno 8 caratteri.</p>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div className="space-y-2"><Label htmlFor="np">Nuova password</Label>
          <Input id="np" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" /></div>
        <div className="space-y-2"><Label htmlFor="cp">Conferma password</Label>
          <Input id="cp" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" /></div>
        {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
        <Button type="submit" className="w-full h-11" disabled={busy} data-testid="reset-submit-button">
          <KeyRound className="h-4 w-4 mr-2" /> {busy ? "Salvataggio…" : "Salva nuova password"}
        </Button>
      </form>
    </Shell>
  );
}
